// Pure helpers for the scope-funnel "define source" flow. No React, no network.

const DIM_DISPLAY = {
  sku_category: "SKU category",
  service: "service",
};
const DIM_DISPLAY_PLURAL = {
  sku_category: "SKU categories",
  service: "services",
};

const sortedCopy = (xs) => [...(xs || [])].sort();
const joinNatural = (items) => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

// Deterministic source_key for ON CONFLICT idempotency. Sort scope, refinements (by
// dim+key), and each refinement's values.
export const buildSourceKey = (criteria) => {
  const aiWorkspaces = sortedCopy(criteria?.scope?.ai_workspaces);
  const refs = [...(criteria?.refinements || [])]
    .filter((r) => (r.values || []).length > 0)
    .map((r) => ({ ...r, values: sortedCopy(r.values) }))
    .sort((a, b) => (a.dim + (a.key || "")).localeCompare(b.dim + (b.key || "")))
    .map((r) => `${r.dim}${r.key ? `:${r.key}` : ""}=${r.values.join(",")}`)
    .join("|");
  // A source scopes ONE axis. The AI-workspace axis gets its own key namespace so it can never
  // collide with a project-scoped key (and so existing project keys stay byte-stable for the
  // ON CONFLICT idempotency).
  let key;
  if (aiWorkspaces.length > 0) {
    key = `scope:ai_workspace:${aiWorkspaces.join(",")}`;
  } else {
    key = `scope:project:${sortedCopy(criteria?.scope?.projects).join(",")}`;
  }
  if (refs) key += `|${refs}`;
  return key;
};

// Renders the plain-language definition statement for the source review panel.
// `labels` optionally maps raw value -> friendly name (for project ids especially).
export const criteriaToStatement = (criteria, typeName, labels = {}) => {
  const lead = typeName ? `All ${typeName} spend` : "All spend";
  const projects = (criteria?.scope?.projects || []);
  const aiWorkspaces = (criteria?.scope?.ai_workspaces || []);
  const refs = (criteria?.refinements || []).filter((r) => (r.values || []).length > 0);

  // Scope phrasing — one axis per source. AI workspaces are a distinct axis from projects.
  let scopeText;
  if (aiWorkspaces.length > 0) {
    const noun = aiWorkspaces.length === 1 ? "AI workspace" : "AI workspaces";
    scopeText = ` in ${noun} ${joinNatural(aiWorkspaces.map((w) => labels[w] || w))}`;
  } else if (projects.length === 0) {
    scopeText = " across every project, including projects added later";
  } else {
    const named = projects.map((p) => labels[p] || p);
    const noun = projects.length === 1 ? "project" : "projects";
    scopeText = ` in ${noun} ${joinNatural(named)}`;
  }

  // Refinement phrasing — one clause per refinement, ANDed.
  // Values inside parens are comma-separated (not natural-joined); the "and" joins clauses.
  const clauses = refs.map((r) => {
    const inside = `(${sortedCopy(r.values).join(", ")})`;
    if (r.dim === "label") return `tagged ${r.key} in ${inside}`;
    const noun = r.values.length === 1 ? DIM_DISPLAY[r.dim] : DIM_DISPLAY_PLURAL[r.dim];
    return `${noun} in ${inside}`;
  });
  const refsText = clauses.length ? `, ${joinNatural(clauses)}` : "";

  return `${lead}${scopeText}${refsText}.`;
};

// Auto-discovered ("classified") sources encode their scope as a cost-base source_key in
// match_expr — e.g. "project:acme". Map that back to the new pick shape so the editor can
// pre-select the project this source actually covers. Returns null for shapes the v1
// builder doesn't represent (e.g. "ai:..." workspaces).
export const classifiedToCriteria = (matchExpr) => {
  if (typeof matchExpr !== "string") return null;
  const i = matchExpr.indexOf(":");
  if (i < 0) return null;
  const prefix = matchExpr.slice(0, i);
  const rest = matchExpr.slice(i + 1);
  if (!rest) return null;
  if (prefix === "project") return { scope: { projects: [rest] }, refinements: [] };
  // "ai:<workspace>" → the AI-workspace scope axis (so detected OpenAI sources are editable).
  if (prefix === "ai") return { scope: { projects: [], ai_workspaces: [rest] }, refinements: [] };
  return null;
};

// Save-gating rule. A "no constraints + Any type" source would match all spend with no
// constraint — exactly the financial footgun the design forbids. The handler also enforces
// this server-side (Task 3), but the UI must reflect it as a disabled Save button.
export const hasConstraint = (criteria, sourceTypeId) => {
  const realType = sourceTypeId && sourceTypeId !== "uncategorized";
  const hasProjects = (criteria?.scope?.projects || []).length > 0;
  const hasAiWorkspaces = (criteria?.scope?.ai_workspaces || []).length > 0;
  const hasRefs = (criteria?.refinements || []).length > 0;
  return Boolean(realType || hasProjects || hasAiWorkspaces || hasRefs);
};

export const buildCreatePayload = (criteria, name, sourceTypeId) => ({
  source_type_id: sourceTypeId || "uncategorized",
  source_key: buildSourceKey(criteria),
  display_name: name,
  match_kind: "criteria",
  match_expr: "",
  metadata: { criteria },
});

// wholeScopeAxisValue returns {axis, value} when a criteria's scope is exactly one
// value on a single axis, else null.
export const wholeScopeAxisValue = (criteria) => {
  const projects = (criteria && criteria.scope && criteria.scope.projects) || [];
  const aiWorkspaces = (criteria && criteria.scope && criteria.scope.ai_workspaces) || [];
  if (projects.length === 1 && aiWorkspaces.length === 0) return { axis: "project", value: projects[0] };
  if (aiWorkspaces.length === 1 && projects.length === 0) return { axis: "ai_workspace", value: aiWorkspaces[0] };
  return null;
};

// isWholeScope reports whether a source's criteria + type describe a whole-scope
// source: a single project/AI-workspace, type "uncategorized", and no refinements.
export const isWholeScope = (criteria, sourceTypeId) => {
  if (!criteria) return false;
  if (sourceTypeId !== "uncategorized") return false;
  if (((criteria.refinements) || []).length > 0) return false;
  return wholeScopeAxisValue(criteria) != null;
};

// For the Advanced (CEL) escape hatch — unchanged.
export const buildCelPayload = (expr, name, sourceTypeId) => ({
  source_type_id: sourceTypeId || "uncategorized",
  source_key: `cel:${expr.slice(0, 40)}`,
  display_name: name,
  match_kind: "cel",
  match_expr: expr,
  metadata: {},
});
